import { useState } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { HardHat } from "lucide-react";
import { useDeck } from "../../lib/deck-context";
import { BUDGET, BUDGET_COLORS, fmtK, fmtM } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

export default function Budget() {
  const { m, inputs } = useDeck();
  const [hover, setHover] = useState(null);
  const lines = BUDGET.map((b, i) => ({ ...b, value: b.amount * m.budgetScale + inputs.budgetDelta[b.key], delta: inputs.budgetDelta[b.key], color: BUDGET_COLORS[i] }));
  const total = inputs.tdc;
  return (
    <Section testid="section-budget">
      <SectionHeader num="06" eyebrow="Development Budget" title="A bottom-up conceptual budget — fully itemized." lede="Every line is shown with its basis so the JV can see exactly what is assumed and what still needs to be validated." status="preliminary" />

      <div className="grid lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-8">
          <Card className="p-8 h-full">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Eyebrow>Preliminary Total Development Cost</Eyebrow>
                <div data-testid="budget-total" className="mt-3 font-display text-6xl text-ink tabular-nums">{fmtM(total)}</div>
              </div>
              <div className="text-right">
                <div data-testid="budget-per-sf" className="font-display text-3xl text-arq tabular-nums">≈ ${Math.round(m.perSf)}</div>
                <div className="text-xs text-steel mt-1">per rentable SF all-in</div>
              </div>
            </div>
            <div data-testid="budget-stacked-bar" className="mt-10 flex h-14 w-full overflow-hidden rounded-lg">
              {lines.map((l, i) => (
                <motion.div
                  key={l.key}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  className="h-full transition-opacity duration-200 cursor-pointer"
                  style={{ background: l.color, opacity: hover === null || hover === i ? 1 : 0.35 }}
                  initial={{ width: 0 }}
                  animate={{ width: `${(l.value / total) * 100}%` }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                />
              ))}
            </div>
            <div className="mt-4 h-10 text-sm">
              {hover !== null ? (
                <span><span className="text-ink font-medium">{lines[hover].label}</span> · <span className="tabular-nums">{fmtM(lines[hover].value)}</span> · <span className="text-steel">{((lines[hover].value / total) * 100).toFixed(1)}% of TDC</span></span>
              ) : <span className="text-steel">Hover a segment or row to inspect.</span>}
            </div>
          </Card>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4">
          <Card className="p-8 h-full flex flex-col items-center justify-center">
            <div className="relative w-full h-[240px]" data-testid="budget-donut">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={lines} dataKey="value" innerRadius="68%" outerRadius="100%" paddingAngle={1.2} stroke="none" onMouseEnter={(_, i) => setHover(i)} onMouseLeave={() => setHover(null)}>
                    {lines.map((l, i) => <Cell key={l.key} fill={l.color} opacity={hover === null || hover === i ? 1 : 0.35} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none text-center">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-steel">{hover !== null ? "SHARE" : "HARD COST"}</div>
                  <div className="font-display text-3xl text-ink tabular-nums">
                    {hover !== null ? `${((lines[hover].value / total) * 100).toFixed(1)}%` : `${(((BUDGET[0].amount + BUDGET[1].amount + BUDGET[2].amount + BUDGET[3].amount) / 12950000) * 100).toFixed(0)}%`}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </Reveal>
      </div>

      <Reveal delay={0.15} className="mt-6">
        <Card className="overflow-x-auto">
          <table data-testid="budget-table" className="data-table w-full min-w-[760px]">
            <thead><tr><th>Line Item</th><th className="!text-right">Working Cost</th><th className="!text-right">% TDC</th><th>Basis / What It Includes</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} data-testid={`budget-row-${l.key}`} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                  <td className="text-ink"><span className="inline-block h-2.5 w-2.5 rounded-sm mr-3 align-middle" style={{ background: l.color }} />{l.label}</td>
                  <td className="text-right tabular-nums text-ink font-medium">
                    {fmtM(l.value)}
                    {l.delta !== 0 && <div data-testid={`budget-delta-${l.key}`} className="text-[11px] font-normal text-arq">{l.delta < 0 ? "−" : "+"}{fmtK(Math.abs(l.delta))} predev line items</div>}
                  </td>
                  <td className="text-right tabular-nums text-steel">{((l.value / total) * 100).toFixed(1)}%</td>
                  <td className="text-steel text-[13px] max-w-[420px]">{l.basis}</td>
                </tr>
              ))}
              <tr className="bg-ink-deep text-white hover:!bg-ink-deep">
                <td className="!py-5 font-mono text-xs tracking-[0.16em]">TOTAL DEVELOPMENT COST</td>
                <td className="!py-5 text-right tabular-nums font-display text-2xl text-arq-mid">{fmtM(total)}</td>
                <td className="!py-5 text-right tabular-nums text-white/60">100%</td>
                <td className="!py-5 text-white/70 text-[13px]">≈ ${Math.round(m.perSf)} per rentable SF all-in · excludes land purchase</td>
              </tr>
            </tbody>
          </table>
        </Card>
      </Reveal>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Reveal delay={0.2}>
          <Card data-testid="budget-next-validation" className="p-7 h-full bg-arq border-arq text-white">
            <HardHat size={22} />
            <div className="mt-5 font-mono text-[10px] tracking-[0.2em] text-white/70">REQUIRED NEXT VALIDATION</div>
            <div className="mt-2 font-display text-2xl leading-snug">General Contractor Preconstruction Pricing</div>
          </Card>
        </Reveal>
        <Note testid="budget-not-a-bid" className="h-full">
          <div className="mb-2"><StatusBadge s="preliminary" /></div>
          This is a conceptual underwriting budget and is not a contractor bid, Guaranteed Maximum Price, or final construction estimate.
        </Note>
        <Note testid="budget-tenant-funded" className="h-full">
          The current development budget assumes tenants/operators fund material portions of their tenant-specific interiors, equipment, furniture, fixtures and specialty build-outs.
        </Note>
      </div>
    </Section>
  );
}
